/**
 * SwimLaneTimeline - D3.js swimlane visualization with Tailwind CSS
 * 5 layers: Energy, Chips, Infrastructure, Model, Applications
 */

import * as d3 from 'd3';
import dataService from './data-service.js';

const LANE_HEIGHT = 140;
const LANE_PADDING = 8;
const LANE_LABEL_WIDTH = 170;
const DOT_MIN_R = 3;
const DOT_MAX_R = 14;
const LABEL_SIG_OFFSET = 2; // labels show for events >= (sigThreshold + LABEL_SIG_OFFSET)
const MARGIN = { top: 56, right: 30, bottom: 44, left: LANE_LABEL_WIDTH };

// Progressive disclosure: zoom scale → minimum significance
// NOTE: initial zoom is ~3.7x (22yr view of 82yr range), so thresholds
// are calibrated around that as the "default" level.
// Distribution: sig>=10: 21, >=9: 84, >=8: 198, >=7: 295, >=5: 472
const SIG_THRESHOLDS = [
  { maxScale: 1.5, sig: 10, labelSig: 10 },    // full range: only 21 landmark events
  { maxScale: 5.0, sig: 9, labelSig: 10 },     // default 22yr view: ~84 major events
  { maxScale: 10.0, sig: 8, labelSig: 9 },     // 2x from default: ~198 events
  { maxScale: 20.0, sig: 7, labelSig: 8 },     // 4x from default: ~295 events
  { maxScale: Infinity, sig: 5, labelSig: 7 },  // max zoom: all 472
];

export class SwimLaneTimeline {
  constructor(container) {
    this.container = container;
    dataService.load();

    this.topCategories = dataService.getTopCategories();
    this.eras = dataService.eras;
    this.allEvents = dataService.events;

    this.filters = { categories: [], search: '', significance: 1, eventTypes: [] };
    this.selectedEvent = null;
    this._manualSig = false; // true when user manually adjusts significance slider
    this._currentZoomScale = 1;

    // Build subcategory layout map: subcat id → { laneIdx, subIdx, subCount }
    this._subcatLayout = new Map();
    this.topCategories.forEach((cat, laneIdx) => {
      const subs = dataService.getSubcategories(cat.id);
      subs.forEach((sub, subIdx) => {
        this._subcatLayout.set(sub.id, { laneIdx, subIdx, subCount: subs.length });
      });
    });

    this.xScale = null;
    this.rScale = d3.scaleSqrt().domain([1, 10]).range([DOT_MIN_R, DOT_MAX_R]);

    this._init();
  }

  _init() {
    this.container.innerHTML = '';
    this.container.className = 'fixed inset-0 overflow-hidden bg-[#0a0e17]';

    this._createControls();
    this._createSVG();
    this._createDetailPanel();
    this._createTooltip();
    this._render();

    window.addEventListener('resize', () => this._resize());
  }

  _getAutoThreshold() {
    for (const t of SIG_THRESHOLDS) {
      if (this._currentZoomScale <= t.maxScale) return t;
    }
    return SIG_THRESHOLDS[SIG_THRESHOLDS.length - 1];
  }

  get filteredEvents() {
    const autoThreshold = this._getAutoThreshold();
    // Use auto threshold unless user manually overrides
    const minSig = this._manualSig ? this.filters.significance : autoThreshold.sig;

    return dataService.filterEvents({
      categories: this.filters.categories.length > 0 ? this.filters.categories : undefined,
      search: this.filters.search || undefined,
      significance: minSig > 1 ? minSig : undefined,
      eventTypes: this.filters.eventTypes.length > 0 ? this.filters.eventTypes : undefined,
    });
  }

  get labelSigThreshold() {
    if (this._manualSig) return this.filters.significance + LABEL_SIG_OFFSET;
    return this._getAutoThreshold().labelSig;
  }

  get width() { return window.innerWidth; }
  get height() { return window.innerHeight; }
  get laneCount() { return this.topCategories.length; }
  get contentHeight() { return MARGIN.top + this.laneCount * LANE_HEIGHT + MARGIN.bottom; }

  // ===================== CONTROLS =====================
  _createControls() {
    const bar = document.createElement('div');
    bar.className = 'fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-5 py-2.5 gap-3 bg-[#0a0e17]/95 backdrop-blur-xl border-b border-white/[0.06]';
    bar.innerHTML = `
      <div class="flex items-center gap-3">
        <div class="text-base font-bold tracking-tight bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent whitespace-nowrap">
          Tech Timeline
        </div>
        <div class="text-xs text-slate-500 whitespace-nowrap font-medium">
          <span id="eventCount" class="text-violet-400 font-semibold">${this.allEvents.length}</span> events
        </div>
      </div>
      <div class="flex items-center gap-4 flex-1 justify-center">
        <div class="flex items-center gap-2 bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 w-56">
          <svg class="text-slate-500 shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input id="tlSearch" type="text" placeholder="Search events..."
            class="bg-transparent border-none outline-none text-slate-200 text-sm w-full font-normal placeholder:text-slate-600" />
        </div>
        <div class="flex items-center gap-2 text-xs">
          <label class="text-slate-400 whitespace-nowrap font-medium">Significance</label>
          <input id="tlSig" type="range" min="1" max="10" value="1"
            class="w-20 accent-violet-500 cursor-pointer" />
          <span id="tlSigVal" class="text-violet-400 font-bold min-w-[14px] text-center">1</span>
        </div>
      </div>
      <div class="flex items-center gap-1.5">
        <button id="tlZoomIn" class="bg-white/[0.06] border border-white/[0.1] text-slate-300 px-3 py-1.5 rounded-md cursor-pointer text-sm font-semibold hover:bg-white/[0.12] hover:text-white transition-all" title="Zoom in">+</button>
        <button id="tlZoomOut" class="bg-white/[0.06] border border-white/[0.1] text-slate-300 px-3 py-1.5 rounded-md cursor-pointer text-sm font-semibold hover:bg-white/[0.12] hover:text-white transition-all" title="Zoom out">&minus;</button>
        <button id="tlReset" class="bg-white/[0.06] border border-white/[0.1] text-slate-300 px-3 py-1.5 rounded-md cursor-pointer text-xs font-semibold hover:bg-white/[0.12] hover:text-white transition-all">Reset</button>
      </div>
    `;
    this.container.appendChild(bar);

    // Category filter chips
    const chips = document.createElement('div');
    chips.className = 'fixed top-[49px] left-0 right-0 z-40 flex gap-2 px-5 py-2 bg-[#0a0e17]/90 backdrop-blur-lg border-b border-white/[0.04] overflow-x-auto hide-scrollbar';
    this.topCategories.forEach(cat => {
      const chip = document.createElement('button');
      chip.className = 'flex items-center gap-1.5 bg-white/[0.04] border border-white/[0.08] text-slate-400 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap hover:bg-white/[0.08] hover:text-slate-200 transition-all';
      chip.dataset.catId = cat.id;
      chip.innerHTML = `<span class="w-2 h-2 rounded-full shrink-0" style="background:${cat.color}"></span>${cat.short_name || cat.name}`;
      chip.onclick = () => this._toggleCategory(cat.id, chip);
      chips.appendChild(chip);
    });
    this.container.appendChild(chips);

    // Event listeners
    document.getElementById('tlSearch').addEventListener('input', (e) => {
      this.filters.search = e.target.value;
      this._updateEvents();
    });
    document.getElementById('tlSig').addEventListener('input', (e) => {
      const val = parseInt(e.target.value);
      this.filters.significance = val;
      this._manualSig = val > 1; // manual mode when user raises threshold
      document.getElementById('tlSigVal').textContent = e.target.value;
      this._updateEvents();
    });
    document.getElementById('tlZoomIn').onclick = () => this._zoom(1.5);
    document.getElementById('tlZoomOut').onclick = () => this._zoom(1 / 1.5);
    document.getElementById('tlReset').onclick = () => this._resetZoom();
  }

  _toggleCategory(catId, chip) {
    const idx = this.filters.categories.indexOf(catId);
    if (idx > -1) {
      this.filters.categories.splice(idx, 1);
      chip.className = chip.className.replace(/bg-violet-500\/20 border-violet-500\/40 text-violet-200/g, 'bg-white/[0.04] border-white/[0.08] text-slate-400');
    } else {
      this.filters.categories.push(catId);
      chip.className = chip.className.replace(/bg-white\/\[0\.04\] border-white\/\[0\.08\] text-slate-400/g, 'bg-violet-500/20 border-violet-500/40 text-violet-200');
    }
    this._updateEvents();
  }

  // ===================== SVG =====================
  _createSVG() {
    const svgWrap = document.createElement('div');
    svgWrap.className = 'fixed top-[85px] left-0 right-0 bottom-0 overflow-hidden';
    this.container.appendChild(svgWrap);
    this.svgWrap = svgWrap;

    this.svg = d3.select(svgWrap)
      .append('svg')
      .attr('class', 'tl-svg');

    this.svg.append('defs')
      .append('clipPath')
      .attr('id', 'chart-clip')
      .append('rect');

    this.bgGroup = this.svg.append('g').attr('class', 'bg-group');
    this.eraGroup = this.svg.append('g').attr('class', 'era-group');
    this.gridGroup = this.svg.append('g').attr('class', 'grid-group');
    this.eventGroup = this.svg.append('g').attr('class', 'event-group').attr('clip-path', 'url(#chart-clip)');
    this.axisGroup = this.svg.append('g').attr('class', 'axis-group');
    this.labelGroup = this.svg.append('g').attr('class', 'label-group');

    this.zoomBehavior = d3.zoom()
      .scaleExtent([0.3, 20])
      .on('zoom', (event) => this._onZoom(event));

    this.svg.call(this.zoomBehavior);
  }

  // ===================== DETAIL PANEL =====================
  _createDetailPanel() {
    const panel = document.createElement('div');
    panel.className = 'fixed top-[85px] right-0 bottom-0 w-[400px] z-50 bg-[#0d1117]/98 backdrop-blur-2xl border-l border-white/[0.08] overflow-y-auto tl-detail-slide translate-x-full';
    panel.id = 'tlDetail';
    panel.innerHTML = `
      <div class="p-6">
        <button id="tlDetailClose" class="absolute top-3 right-3 bg-white/[0.06] border border-white/[0.1] text-slate-400 w-8 h-8 rounded-lg text-lg cursor-pointer flex items-center justify-center hover:bg-white/[0.12] hover:text-white transition-all">&times;</button>
        <div id="tlDetailBody"></div>
      </div>
    `;
    this.container.appendChild(panel);
    document.getElementById('tlDetailClose').onclick = () => this._closeDetail();
  }

  _createTooltip() {
    this.tooltip = d3.select(this.container)
      .append('div')
      .attr('class', 'tl-tooltip rounded-xl border border-white/[0.12] bg-[#0d1117]/95 backdrop-blur-xl px-4 py-3 max-w-[300px] shadow-2xl')
      .style('opacity', 0);
  }

  // ===================== RENDER =====================
  _render() {
    const w = this.width;
    const h = this.height;
    const chartW = w - MARGIN.left - MARGIN.right;
    const chartH = this.laneCount * LANE_HEIGHT;

    const years = this.allEvents.map(e => e.date.year);
    const minYear = Math.min(...years) - 1;
    const maxYear = Math.max(...years) + 1;
    this.xScale = d3.scaleLinear().domain([minYear, maxYear]).range([0, chartW]);

    this.svg
      .attr('width', w)
      .attr('height', Math.max(h, MARGIN.top + chartH + MARGIN.bottom));

    this.svg.select('#chart-clip rect')
      .attr('x', MARGIN.left).attr('y', MARGIN.top)
      .attr('width', chartW).attr('height', chartH);

    // On first render, compute initial zoom to focus on recent years
    if (!this._initialZoomApplied) {
      this._initialZoomApplied = true;
      const currentYear = new Date().getFullYear();
      const viewYears = 22;
      const startYear = currentYear - viewYears;
      const targetX1 = this.xScale(startYear);
      const targetX2 = this.xScale(currentYear + 0.5);
      const scale = chartW / (targetX2 - targetX1);
      const tx = -targetX1 * scale;
      this._currentTransform = d3.zoomIdentity.translate(tx, 0).scale(scale);
      this._currentZoomScale = scale; // Track for progressive disclosure
      // Store the zoomed scale for rendering
      this._zoomedXScale = this._currentTransform.rescaleX(this.xScale);
    }

    this._renderEras(chartW, chartH);
    this._renderLanes(chartW, chartH);
    this._renderDensity(chartW, chartH);
    this._renderAxis(chartW, chartH);
    this._renderEvents();

    // Apply the zoom transform to D3 zoom behavior (without re-triggering _onZoom)
    if (this._currentTransform && !this._zoomSynced) {
      this._zoomSynced = true;
      this.svg.call(this.zoomBehavior.transform, this._currentTransform);
    }
  }

  get activeXScale() {
    return this._zoomedXScale || this.xScale;
  }

  _renderEras(chartW, chartH) {
    const eras = this.eras.filter(e => !e.is_projection);
    const g = this.eraGroup;
    g.selectAll('*').remove();
    const xs = this.activeXScale;

    eras.forEach(era => {
      const x1 = MARGIN.left + Math.max(0, xs(era.start_year));
      const x2 = MARGIN.left + Math.min(chartW, xs(era.end_year));
      if (x2 <= x1) return;

      g.append('rect')
        .attr('x', x1).attr('y', MARGIN.top)
        .attr('width', x2 - x1).attr('height', chartH)
        .attr('fill', era.color).attr('opacity', 0.05);

      g.append('text')
        .attr('x', (x1 + x2) / 2).attr('y', MARGIN.top - 8)
        .attr('text-anchor', 'middle')
        .attr('fill', era.color)
        .attr('font-size', 10)
        .attr('font-weight', 500)
        .attr('opacity', 0.6)
        .text(era.name);
    });
  }

  _renderLanes(chartW, chartH) {
    const bg = this.bgGroup;
    const labels = this.labelGroup;
    bg.selectAll('*').remove();
    labels.selectAll('*').remove();

    this.topCategories.forEach((cat, i) => {
      const y = MARGIN.top + i * LANE_HEIGHT;

      // Alternating subtle background
      bg.append('rect')
        .attr('x', MARGIN.left).attr('y', y)
        .attr('width', chartW).attr('height', LANE_HEIGHT)
        .attr('fill', i % 2 === 0 ? 'rgba(255,255,255,0.015)' : 'transparent');

      // Lane divider
      bg.append('line')
        .attr('x1', MARGIN.left).attr('x2', MARGIN.left + chartW)
        .attr('y1', y).attr('y2', y)
        .attr('stroke', 'rgba(255,255,255,0.05)');

      // Color accent bar (left edge)
      labels.append('rect')
        .attr('x', 0).attr('y', y + 4)
        .attr('width', 4).attr('height', LANE_HEIGHT - 8)
        .attr('fill', cat.color)
        .attr('rx', 2);

      // Category icon background
      labels.append('rect')
        .attr('x', 14).attr('y', y + LANE_HEIGHT / 2 - 16)
        .attr('width', 32).attr('height', 32)
        .attr('fill', cat.color)
        .attr('opacity', 0.12)
        .attr('rx', 8);

      // Category label - high contrast white
      labels.append('text')
        .attr('x', 56).attr('y', y + LANE_HEIGHT / 2 - 4)
        .attr('dominant-baseline', 'middle')
        .attr('fill', '#f1f5f9')
        .attr('font-size', 14)
        .attr('font-weight', 700)
        .attr('letter-spacing', '-0.01em')
        .text(cat.short_name || cat.name);

      // Event count
      labels.append('text')
        .attr('class', `lane-count-${cat.id}`)
        .attr('x', 56).attr('y', y + LANE_HEIGHT / 2 + 14)
        .attr('dominant-baseline', 'middle')
        .attr('fill', 'rgba(148,163,184,0.7)')
        .attr('font-size', 11)
        .attr('font-weight', 500);
    });

    // Bottom border
    bg.append('line')
      .attr('x1', MARGIN.left).attr('x2', MARGIN.left + chartW)
      .attr('y1', MARGIN.top + chartH).attr('y2', MARGIN.top + chartH)
      .attr('stroke', 'rgba(255,255,255,0.05)');
  }

  _renderAxis(chartW, chartH) {
    const g = this.axisGroup;
    g.selectAll('*').remove();

    const xs = this.activeXScale;
    const axis = d3.axisBottom(xs)
      .tickFormat(d => d.toString())
      .ticks(Math.min(chartW / 60, 40));

    g.attr('transform', `translate(${MARGIN.left}, ${MARGIN.top + chartH})`)
      .call(axis)
      .selectAll('text')
      .attr('fill', 'rgba(148,163,184,0.6)')
      .attr('font-size', 11)
      .attr('font-weight', 500);

    g.selectAll('line').attr('stroke', 'rgba(255,255,255,0.08)');
    g.select('.domain').attr('stroke', 'rgba(255,255,255,0.08)');

    const grid = this.gridGroup;
    grid.selectAll('*').remove();
    const ticks = xs.ticks(Math.min(chartW / 60, 40));
    ticks.forEach(tick => {
      grid.append('line')
        .attr('x1', MARGIN.left + xs(tick))
        .attr('x2', MARGIN.left + xs(tick))
        .attr('y1', MARGIN.top).attr('y2', MARGIN.top + chartH)
        .attr('stroke', 'rgba(255,255,255,0.03)');
    });
  }

  _positionEvent(evt) {
    const topCat = dataService.getTopCategory(evt.category);
    const laneMap = this._laneMap;
    const laneIdx = topCat ? laneMap.get(topCat.id) : undefined;
    if (laneIdx === undefined) return null;

    const month = evt.date.month || 6;
    const xVal = evt.date.year + (month - 1) / 12;
    const x = MARGIN.left + this.activeXScale(xVal);
    const laneY = MARGIN.top + laneIdx * LANE_HEIGHT;

    // Subcategory-based vertical positioning
    const subLayout = this._subcatLayout.get(evt.category);
    let y;
    if (subLayout && subLayout.subCount > 0) {
      const bandHeight = (LANE_HEIGHT - LANE_PADDING * 2) / subLayout.subCount;
      const bandTop = laneY + LANE_PADDING + subLayout.subIdx * bandHeight;
      // Small jitter within band based on event hash
      const jitter = (Math.abs(this._hash(evt.id)) % Math.max(1, Math.floor(bandHeight - DOT_MAX_R))) ;
      y = bandTop + Math.min(jitter, bandHeight - DOT_MIN_R);
    } else {
      // Fallback: distribute evenly
      const yOffset = LANE_PADDING + (Math.abs(this._hash(evt.id)) % (LANE_HEIGHT - LANE_PADDING * 2));
      y = laneY + yOffset;
    }

    const r = this.rScale(evt.significance);
    const color = dataService.getCategoryColor(evt.category);
    return { evt, x, y, r, color, laneIdx };
  }

  _renderDensity(chartW, chartH) {
    // Remove old density elements
    this.bgGroup.selectAll('.density-rect').remove();

    const xs = this.activeXScale;
    const domain = xs.domain();
    const binWidth = 2; // years per bin
    const laneMap = this._laneMap;

    this.topCategories.forEach((cat, i) => {
      // Count events per bin for this lane
      const laneEvents = this.allEvents.filter(e => {
        const tc = dataService.getTopCategory(e.category);
        return tc && tc.id === cat.id;
      });

      const bins = {};
      let maxCount = 0;
      for (const evt of laneEvents) {
        const bin = Math.floor(evt.date.year / binWidth) * binWidth;
        bins[bin] = (bins[bin] || 0) + 1;
        if (bins[bin] > maxCount) maxCount = bins[bin];
      }

      if (maxCount === 0) return;
      const laneY = MARGIN.top + i * LANE_HEIGHT;

      for (const [bin, count] of Object.entries(bins)) {
        const bYear = parseInt(bin);
        const x1 = MARGIN.left + Math.max(0, xs(bYear));
        const x2 = MARGIN.left + Math.min(chartW, xs(bYear + binWidth));
        if (x2 <= x1) continue;

        const opacity = 0.02 + (count / maxCount) * 0.10;
        this.bgGroup.append('rect')
          .attr('class', 'density-rect')
          .attr('x', x1).attr('y', laneY)
          .attr('width', x2 - x1).attr('height', LANE_HEIGHT)
          .attr('fill', cat.color)
          .attr('opacity', opacity)
          .attr('pointer-events', 'none');
      }
    });
  }

  _renderEvents() {
    const events = this.filteredEvents;
    const g = this.eventGroup;

    // Build lane map
    if (!this._laneMap) {
      this._laneMap = new Map();
      this.topCategories.forEach((cat, i) => this._laneMap.set(cat.id, i));
    }

    const positioned = events.map(evt => this._positionEvent(evt)).filter(Boolean);

    // Update lane counts
    const laneCounts = {};
    positioned.forEach(p => {
      const catId = this.topCategories[p.laneIdx].id;
      laneCounts[catId] = (laneCounts[catId] || 0) + 1;
    });
    this.topCategories.forEach(cat => {
      this.labelGroup.select(`.lane-count-${cat.id}`)
        .text(`${laneCounts[cat.id] || 0} event${(laneCounts[cat.id] || 0) === 1 ? '' : 's'}`);
    });

    document.getElementById('eventCount').textContent = events.length;

    // --- Dots ---
    const dots = g.selectAll('.event-dot')
      .data(positioned, d => d.evt.id);

    dots.exit()
      .transition().duration(300)
      .attr('r', 0).attr('opacity', 0)
      .remove();

    const enter = dots.enter()
      .append('circle')
      .attr('class', 'event-dot')
      .attr('cx', d => d.x).attr('cy', d => d.y)
      .attr('r', 0)
      .attr('fill', d => d.color)
      .attr('opacity', 0.8)
      .attr('stroke', 'none')
      .attr('cursor', 'pointer')
      .on('mouseenter', (event, d) => this._onDotHover(event, d))
      .on('mouseleave', () => this._onDotLeave())
      .on('click', (event, d) => this._onDotClick(d));

    enter.transition().duration(400)
      .attr('r', d => d.r).attr('opacity', 0.85);

    dots.transition().duration(300)
      .attr('cx', d => d.x).attr('cy', d => d.y)
      .attr('r', d => d.r).attr('fill', d => d.color)
      .attr('opacity', 0.85);

    // --- Text Labels for high-significance events (with collision avoidance) ---
    const labelThreshold = this.labelSigThreshold;
    const labelCandidates = positioned
      .filter(d => d.evt.significance >= labelThreshold)
      .sort((a, b) => b.evt.significance - a.evt.significance || a.x - b.x);

    // Greedy collision avoidance: only show labels that don't overlap
    const LABEL_H = 12;
    const CHAR_W = 6;
    const placed = [];
    const labelData = [];
    for (const d of labelCandidates) {
      const lx = d.x + d.r + 4;
      const ly = d.y + 3;
      const text = d.evt.title.length > 20 ? d.evt.title.slice(0, 18) + '...' : d.evt.title;
      const lw = text.length * CHAR_W;
      const rect = { x1: lx, y1: ly - LABEL_H, x2: lx + lw, y2: ly + 2 };
      const overlaps = placed.some(p =>
        rect.x1 < p.x2 && rect.x2 > p.x1 && rect.y1 < p.y2 && rect.y2 > p.y1
      );
      if (!overlaps) {
        placed.push(rect);
        labelData.push({ ...d, labelText: text, labelX: lx, labelY: ly });
      }
    }

    const labels = g.selectAll('.event-label')
      .data(labelData, d => d.evt.id);

    labels.exit()
      .transition().duration(200)
      .attr('opacity', 0)
      .remove();

    const labelEnter = labels.enter()
      .append('text')
      .attr('class', 'event-label')
      .attr('x', d => d.labelX)
      .attr('y', d => d.labelY)
      .attr('fill', '#e2e8f0')
      .attr('font-size', 10)
      .attr('font-weight', 600)
      .attr('opacity', 0)
      .attr('pointer-events', 'none')
      .style('text-shadow', '0 1px 3px rgba(0,0,0,0.8), 0 0px 6px rgba(0,0,0,0.6)')
      .text(d => d.labelText);

    labelEnter.transition().duration(400)
      .attr('opacity', 0.9);

    labels.transition().duration(300)
      .attr('x', d => d.labelX)
      .attr('y', d => d.labelY)
      .attr('opacity', 0.9)
      .text(d => d.labelText);
  }

  _hash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash |= 0;
    }
    return hash;
  }

  // ===================== INTERACTIONS =====================
  _onZoom(event) {
    const { transform } = event;
    const chartW = this.width - MARGIN.left - MARGIN.right;
    const newXScale = transform.rescaleX(this.xScale);
    this._zoomedXScale = newXScale;

    // Track zoom scale for progressive disclosure
    const prevThreshold = this._getAutoThreshold();
    this._currentZoomScale = transform.k;
    const newThreshold = this._getAutoThreshold();

    const axis = d3.axisBottom(newXScale)
      .tickFormat(d => d.toString())
      .ticks(Math.min(chartW / 60, 40));
    this.axisGroup.call(axis);
    this.axisGroup.selectAll('text').attr('fill', 'rgba(148,163,184,0.6)').attr('font-size', 11).attr('font-weight', 500);
    this.axisGroup.selectAll('line').attr('stroke', 'rgba(255,255,255,0.08)');
    this.axisGroup.select('.domain').attr('stroke', 'rgba(255,255,255,0.08)');

    this.gridGroup.selectAll('*').remove();
    const chartH = this.laneCount * LANE_HEIGHT;
    const ticks = newXScale.ticks(Math.min(chartW / 60, 40));
    ticks.forEach(tick => {
      this.gridGroup.append('line')
        .attr('x1', MARGIN.left + newXScale(tick))
        .attr('x2', MARGIN.left + newXScale(tick))
        .attr('y1', MARGIN.top).attr('y2', MARGIN.top + chartH)
        .attr('stroke', 'rgba(255,255,255,0.03)');
    });

    this.eraGroup.selectAll('*').remove();
    this.eras.filter(e => !e.is_projection).forEach(era => {
      const x1 = MARGIN.left + Math.max(0, newXScale(era.start_year));
      const x2 = MARGIN.left + Math.min(chartW, newXScale(era.end_year));
      if (x2 <= x1) return;
      this.eraGroup.append('rect')
        .attr('x', x1).attr('y', MARGIN.top)
        .attr('width', x2 - x1).attr('height', chartH)
        .attr('fill', era.color).attr('opacity', 0.05);
      this.eraGroup.append('text')
        .attr('x', (x1 + x2) / 2).attr('y', MARGIN.top - 8)
        .attr('text-anchor', 'middle')
        .attr('fill', era.color).attr('font-size', 10).attr('font-weight', 500).attr('opacity', 0.6)
        .text(era.name);
    });

    // Update density heatmap
    this._renderDensity(chartW, chartH);

    // Always re-render events on zoom (label collision detection depends on x positions)
    this._renderEvents();
  }

  _zoom(factor) {
    this.svg.transition().duration(300).call(this.zoomBehavior.scaleBy, factor);
  }

  _resetZoom() {
    this._zoomedXScale = null;
    this._currentZoomScale = 1;
    this._manualSig = false;
    this.filters.significance = 1;
    document.getElementById('tlSig').value = 1;
    document.getElementById('tlSigVal').textContent = '1';
    this.svg.transition().duration(500).call(this.zoomBehavior.transform, d3.zoomIdentity);
  }

  _onDotHover(event, d) {
    const cat = dataService.categoryMap.get(d.evt.category);
    const company = (d.evt.entities?.companies || [])
      .map(id => dataService.companyMap.get(id)?.name).filter(Boolean).join(', ');

    this.tooltip
      .style('opacity', 1)
      .html(`
        <div class="text-sm font-semibold text-slate-100 mb-1">${d.evt.title}</div>
        <div class="text-xs text-slate-400">${d.evt.date.year}${d.evt.date.month ? '/' + d.evt.date.month : ''} &middot; ${cat?.name || d.evt.category}</div>
        ${company ? `<div class="text-xs text-slate-500 mt-0.5">${company}</div>` : ''}
        <div class="text-[10px] text-amber-400 mt-1 tracking-wider">${'★'.repeat(Math.min(d.evt.significance, 10))}</div>
      `)
      .style('left', (event.pageX + 14) + 'px')
      .style('top', (event.pageY - 12) + 'px');

    d3.select(event.target)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .attr('opacity', 1)
      .raise();
  }

  _onDotLeave() {
    this.tooltip.style('opacity', 0);
    this.eventGroup.selectAll('.event-dot')
      .attr('stroke', 'none')
      .attr('opacity', 0.85);
  }

  _onDotClick(d) {
    this.selectedEvent = d.evt;
    this._showDetail(d.evt);
  }

  _showDetail(evt) {
    const panel = document.getElementById('tlDetail');
    const body = document.getElementById('tlDetailBody');
    const cat = dataService.categoryMap.get(evt.category);
    const topCat = dataService.getTopCategory(evt.category);
    const companies = (evt.entities?.companies || [])
      .map(id => dataService.companyMap.get(id)).filter(Boolean);

    let metricsHtml = '';
    if (evt.metrics) {
      const items = [];
      if (evt.metrics.valuation_usd) items.push(`Valuation: $${(evt.metrics.valuation_usd / 1e9).toFixed(1)}B`);
      if (evt.metrics.funding_usd) items.push(`Funding: $${(evt.metrics.funding_usd / 1e9).toFixed(1)}B`);
      if (evt.metrics.parameters) items.push(`Parameters: ${(evt.metrics.parameters / 1e9).toFixed(1)}B`);
      if (evt.metrics.users) items.push(`Users: ${(evt.metrics.users / 1e6).toFixed(0)}M`);
      if (items.length) metricsHtml = `<div class="text-xs text-violet-300 bg-violet-500/[0.08] border border-violet-500/20 px-3 py-2 rounded-lg mb-4 font-medium">${items.join(' &middot; ')}</div>`;
    }

    body.innerHTML = `
      <div class="text-xs font-bold uppercase tracking-wider mb-2" style="color:${topCat?.color || '#94a3b8'}">${topCat?.name || ''} &rsaquo; ${cat?.name || evt.category}</div>
      <h2 class="text-xl font-bold text-slate-100 mb-2 leading-tight">${evt.title}</h2>
      <div class="text-sm text-slate-400 mb-1 font-medium">${evt.date.year}${evt.date.month ? '/' + String(evt.date.month).padStart(2, '0') : ''}</div>
      <div class="text-xs text-slate-500 capitalize mb-4">${evt.event_type.replace(/_/g, ' ')} &middot; Significance: <span class="text-amber-400 font-semibold">${evt.significance}/10</span></div>
      ${metricsHtml}
      <p class="text-sm text-slate-300 leading-relaxed mb-5">${evt.description || 'No description available.'}</p>
      ${companies.length ? `<div class="flex flex-wrap gap-2 mb-4">${companies.map(c => `<span class="text-xs text-violet-200 bg-violet-500/10 border border-violet-500/20 px-3 py-1 rounded-full font-medium">${c.display_name || c.name}</span>`).join('')}</div>` : ''}
      ${evt.tags?.length ? `<div class="flex flex-wrap gap-1.5 mb-4">${evt.tags.map(t => `<span class="text-[10px] text-slate-400 bg-white/[0.04] border border-white/[0.06] px-2 py-0.5 rounded-full">${t}</span>`).join('')}</div>` : ''}
      ${evt.sources?.length ? `<a class="inline-block text-xs text-violet-400 border border-violet-500/30 px-4 py-2 rounded-lg hover:bg-violet-500/10 transition-all font-medium" href="${evt.sources[0].url}" target="_blank" rel="noopener">View Source &rarr;</a>` : ''}
    `;

    panel.classList.remove('translate-x-full');
    panel.classList.add('translate-x-0');
  }

  _closeDetail() {
    const panel = document.getElementById('tlDetail');
    panel.classList.remove('translate-x-0');
    panel.classList.add('translate-x-full');
    this.selectedEvent = null;
  }

  _updateEvents() {
    this._renderEvents();
  }
  _resize() { this._render(); }
}
